import { Stack } from 'component-library';
import { useEffect } from 'react';

import { PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GradientScale } from '@/components/widgets/o/Gradient/Gradient';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';

import { useSetIndicatorHasData } from './IndicatorContextProvider';

export type GradientContentProps = {
  contentProps: {
    sourceRef?: string;
    facts?: {
      name: string;
      unit: string;
      title: string;
      tooltip: string;
      gradientColors?: string;
      min?: number;
      max?: number;
    }[];
  };
  plotGroupId?: string;
};

export const GradientScaleTile = ({ contentProps, plotGroupId }: GradientContentProps) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();
  const facts = contentProps?.facts ?? [];
  const factNames = facts?.map((fact) => fact.name) ?? [];

  if (!factNames || !Array.isArray(factNames) || factNames.length === 0) {
    throw new Error('No factName provided for GradientScaleTile');
  }

  const geomodelServerOptions = {
    source_ref: contentProps?.sourceRef,
  };

  const maxFacts = useOrganizationIndicatorReportingFact(
    factNames,
    null,
    {
      [PlotReportFactLabelNameEnum.statistic]: 'max',
    },
    plotGroupId,
    true,
    geomodelServerOptions,
  );
  const meanFacts = useOrganizationIndicatorReportingFact(
    factNames,
    null,
    {
      [PlotReportFactLabelNameEnum.statistic]: 'mean',
    },
    plotGroupId,
    true,
    geomodelServerOptions,
  );
  const minFacts = useOrganizationIndicatorReportingFact(
    factNames,
    null,
    {
      [PlotReportFactLabelNameEnum.statistic]: 'min',
    },
    plotGroupId,
    true,
    geomodelServerOptions,
  );

  const resolvedValues: {
    value: number | null;
    min: number | null;
    max: number | null;
    unit?: string;
    title?: string;
    tooltip?: string;
    gradientColors?: string;
  }[] = [];
  facts.forEach((f) => {
    const meanFact = meanFacts.find((fact) => fact.name === f.name)?.value as number | undefined;
    const minFact = isNullish(f?.min)
      ? (minFacts.find((fact) => fact.name === f.name)?.value as number | undefined)
      : f.min;
    const maxFact = isNullish(f?.max)
      ? (maxFacts.find((fact) => fact.name === f.name)?.value as number | undefined)
      : f.max;

    if (isNullish(meanFact) || isNullish(minFact) || isNullish(maxFact)) {
      return;
    }

    resolvedValues.push({
      value: meanFact ?? null,
      min: minFact ?? null,
      max: maxFact ?? null,
      unit: f?.unit,
      title: f?.title,
      tooltip: f?.tooltip,
      gradientColors: f?.gradientColors,
    });
  });

  const noData = resolvedValues.length === 0;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noData });
  }, [noData, setIndicatorGraphData]);

  return (
    <Stack center>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        {resolvedValues.map((fact, index) => (
          <GradientScale
            key={`gradient-scale-${index}`}
            value={fact.value ?? null}
            min={fact.min ?? null}
            max={fact.max ?? null}
            title={fact?.title}
            tooltip={fact?.tooltip}
            unit={fact?.unit}
            gradientColors={fact?.gradientColors}
            className='w-full px-6 sm:px-0'
          />
        ))}
      </Stack>
    </Stack>
  );
};
